import React, { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  Column,
  FormController,
  MuiBox,
  MuiButtonSecondary,
  MuiGrid,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  OptionsDataType,
  RowProps,
} from "../../../../../../../../../components";
import PermissionWrapper from "../../../../../../../../../components/permissionWrapper/PermissionWrapper";
import {
  EMPTY_VALUE_DASH,
  MAX_PERCENTAGE,
  MODULE_PERMISSIONS,
} from "../../../../../../../../../helpers/constants";
import {
  MenuItemConfig,
  UserDetails,
} from "../../../../../../../../../helpers/types/common";
import {
  buildActionMenuItems,
  formatApiDataToDropDownOption,
  getValueOrDash,
  isNotEmpty,
} from "../../../../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../../../../../../hooks/useNavigationSiteCore";
import { usePermissions } from "../../../../../../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../../../../../../hooks/useSiteCore";
import {
  SalesCreditDetailsFormKeys,
  SalesCreditTableData,
} from "../../../../../../../../createProgram/modelsAndSchemas/Models";
import { salesCreditSiteCoreKeys } from "../../../../../../../siteCore/SiteCoreKeys";
import { SalesCreditDetailsTableWrapper } from "./SalesCreditDetailsTable.style";
import { SalesCreditDetailsTableHeader } from "./salesCreditDetailsTableHeader/SalesCreditDetailsTableHeader";

const MAX_SALES_CREDIT_ENTRIES = 3;
interface SalesCreditDetailsTableProps {
  pageId: string;
  addSalesCredit: () => void;
  filteredResults: SalesCreditTableData[];
  onDeleteSalesCreditEntryAction: (row: SalesCreditTableData) => void;
  onSalesCreditAddedCallback: boolean;
  clientRelationshipOwnerList?: UserDetails[];
  onSalesCreditDeletedCallback: boolean;
  isInViewMode?: boolean;
  showDefaultEntry: boolean;
}

export const SalesCreditDetailsTable: React.FC<
  SalesCreditDetailsTableProps
> = ({
  addSalesCredit,
  filteredResults,
  onDeleteSalesCreditEntryAction,
  onSalesCreditAddedCallback,
  clientRelationshipOwnerList,
  onSalesCreditDeletedCallback,
  pageId,
  showDefaultEntry,
  isInViewMode = false,
}) => {
  const { gridSizeMedium: gridSize } = useGridContainer();

  const { control, setValue } = useFormContext();
  const { commonLabels, fetchLabel } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { hasModulePermission } = usePermissions();

  const getLabel = (key: string) => {
    return fetchLabel(salesCreditSiteCoreKeys.salesCredit, key);
  };

  const clientRelationshipOwnerKey =
    SalesCreditDetailsFormKeys.clientRelationshipOwner;
  const percentageKey = SalesCreditDetailsFormKeys.percentage;
  const officeNameKey = SalesCreditDetailsFormKeys.officeName;

  const watchClientRelationshipOwner = useWatch({
    control,
    name: SalesCreditDetailsFormKeys.clientRelationshipOwner,
  });
  const [
    clientRelationshipOwnerDropdownData,
    setClientRelationshipOwnerDropdownData,
  ] = useState<OptionsDataType[]>([]);

  const FIXED_SALES_CREDIT_PERCENTAGE: OptionsDataType[] = [
    "0",
    "30",
    "33",
    "34",
    "50",
    "67",
    "70",
    "100",
  ].map((value) => ({
    label: value.toString(),
    value: value,
  }));

  const MAP_33_34_67 = ["33", "34", "67"].map((value) => ({
    label: value.toString(),
    value: value,
  }));

  const VALUE_MAPPER: Record<string, string[]> = {
    "0": ["100"],
    "100": ["0"],
    "30": ["70"],
    "70": ["30"],
    "67": ["33"],
    "33": ["33", "34", "67"],
    "34": ["33"],
    "50": ["50"],
  };

  const [percentageDropdownData, setPercentageDropdownData] = useState<
    OptionsDataType[]
  >(FIXED_SALES_CREDIT_PERCENTAGE);

  const onClientRelationshipOwnerChange = (selectedValue: string) => {
    const getClientRelationshipOwnerData = clientRelationshipOwnerList?.find(
      (clientRelationshipOwner) => clientRelationshipOwner.id === selectedValue,
    );
    if (isNotEmpty(getClientRelationshipOwnerData)) {
      const officeNameValue = getValueOrDash(
        getClientRelationshipOwnerData?.officeName,
      );

      setValue(officeNameKey, officeNameValue);
    }
  };

  useEffect(() => {
    onClientRelationshipOwnerChange(watchClientRelationshipOwner);
  }, [watchClientRelationshipOwner]);

  useEffect(() => {
    setClientRelationshipOwnerDropdownData(
      formatApiDataToDropDownOption(clientRelationshipOwnerList ?? []),
    );
  }, [clientRelationshipOwnerList]);

  const updatePercentageDropdownData = () => {
    const filteredResultsCopy = [...filteredResults];

    if (filteredResultsCopy.length === 1) {
      const [first] = filteredResultsCopy;
      const mappedValues = VALUE_MAPPER[first.percentage] || [];
      setPercentageDropdownData(
        mappedValues.map((value) => ({
          label: value.toString(),
          value: value,
        })),
      );
      return;
    }

    if (filteredResultsCopy.length === 2) {
      const totalAssigned = filteredResultsCopy.reduce(
        (sum, item) => sum + (parseInt(item.percentage ?? "0") || 0),
        0,
      );
      const remaining = MAX_PERCENTAGE - totalAssigned;
      setPercentageDropdownData(
        MAP_33_34_67.filter((option) => Number(option.value) === remaining),
      );
      return;
    }

    setPercentageDropdownData(
      filteredResultsCopy.length === 0 ? FIXED_SALES_CREDIT_PERCENTAGE : [],
    );
  };

  useEffect(() => {
    updateClientRelationshipOwnerDropdownData();
    updatePercentageDropdownData();
  }, [
    onSalesCreditAddedCallback,
    onSalesCreditDeletedCallback,
    filteredResults,
  ]);

  const updateClientRelationshipOwnerDropdownData = () => {
    const filteredResultsCopy = [...filteredResults];

    const uniqueClientRelationshipOwner = (
      clientRelationshipOwnerList ?? []
    ).filter((clientRelationshipOwner) => {
      return !filteredResultsCopy.some((result) =>
        result.localClientRelationshipOwnerId?.includes(
          clientRelationshipOwner.id,
        ),
      );
    });

    setClientRelationshipOwnerDropdownData(
      formatApiDataToDropDownOption(uniqueClientRelationshipOwner ?? []),
    );
  };

  const calculateTotalPercentage = (
    filteredResults: SalesCreditTableData[],
  ) => {
    let totalAmountOrPercentage = 0;
    filteredResults.forEach((item) => {
      totalAmountOrPercentage += parseInt(item?.percentage ?? "0");
    });
    return totalAmountOrPercentage;
  };

  const columns: Column[] = [
    {
      id: clientRelationshipOwnerKey,
      label: getLabel(
        salesCreditSiteCoreKeys.clientRelationshipOwnerTableColumn,
      ),
      isSortVisible: false,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">
            {row.clientRelationshipOwner}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: officeNameKey,
      label: getLabel(salesCreditSiteCoreKeys.officeTableColumn),
      isSortVisible: false,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">{row.officeName}</MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: percentageKey,
      label: getLabel(salesCreditSiteCoreKeys.percentageTableColumn),
      isSortVisible: false,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">{`${row.percentage}%`}</MuiTypography>
        </MuiBox>
      ),
    },
    ...(!isInViewMode
      ? [
          {
            id: "action",
            label: getLabel(salesCreditSiteCoreKeys.actionTableColumn),
            render: (row: any) => {
              const salesCreditActions: MenuItemConfig<RowProps>[] = [
                {
                  permissionKey: MODULE_PERMISSIONS.EDIT_SALES_CREDIT,
                  create: (r) => ({
                    label: commonLabels.delete,
                    dataTestId: `${pageId}-delete`,
                    onClick: () => onDeleteSalesCreditEntryAction(row),
                  }),
                },
              ];

              const salesCreditMenuItems = buildActionMenuItems(
                row,
                salesCreditActions,
                hasModulePermission,
              );

              return salesCreditMenuItems ? (
                <MuiOptionMenu menuItems={salesCreditMenuItems} />
              ) : null;
            },
          },
        ]
      : []),
  ];

  const disabledAddButton = () => {
    return (
      showDefaultEntry ||
      ((filteredResults.length >= MAX_SALES_CREDIT_ENTRIES ||
        calculateTotalPercentage(filteredResults) >= MAX_PERCENTAGE) &&
        filteredResults.length !== 1)
    );
  };

  return (
    <SalesCreditDetailsTableWrapper>
      {!isInViewMode ? (
        <>
          <SalesCreditDetailsTableHeader
            header={getLabel(
              salesCreditSiteCoreKeys.addClientRelationshipOwnerSubHeading,
            )}
          />
          <MuiBox className="sales-credit-configuration-middle">
            <MuiGrid container spacing={2}>
              <MuiGrid size={gridSize}>
                <FormController
                  control={control}
                  name={clientRelationshipOwnerKey}
                >
                  <MuiSelect
                    id={`${pageId}-${clientRelationshipOwnerKey}`}
                    label={getLabel(
                      salesCreditSiteCoreKeys.clientRelationshipOwnerDropDown,
                    )}
                    options={clientRelationshipOwnerDropdownData}
                    visibleDefaultSelectOption={true}
                    placeholder={commonLabels.selectPlaceHolder}
                    isMandatory
                    onChange={(event) => {
                      onClientRelationshipOwnerChange(
                        event.target.value as string,
                      );
                    }}
                    requiredARIALabel={commonARIALabel.required}
                    disabled={disabledAddButton()}
                  />
                </FormController>
              </MuiGrid>
              <MuiGrid size={gridSize}>
                <FormController control={control} name={officeNameKey}>
                  <MuiTextField
                    id={`${pageId}-${officeNameKey}`}
                    label={getLabel(salesCreditSiteCoreKeys.office)}
                    placeholder={EMPTY_VALUE_DASH}
                    disabled
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
              </MuiGrid>

              <MuiGrid size={gridSize}>
                <FormController control={control} name={percentageKey}>
                  <MuiSelect
                    id={`${pageId}-${percentageKey}`}
                    label={getLabel(salesCreditSiteCoreKeys.percentageDropDown)}
                    options={percentageDropdownData}
                    visibleDefaultSelectOption={true}
                    placeholder={commonLabels.selectPlaceHolder}
                    isMandatory
                    requiredARIALabel={commonARIALabel.required}
                    disabled={disabledAddButton()}
                  />
                </FormController>
              </MuiGrid>
            </MuiGrid>

            <PermissionWrapper permission={MODULE_PERMISSIONS.ADD_SALES_CREDIT}>
              <MuiBox className="sales-credit-configuration-add-button">
                <MuiButtonSecondary
                  onClick={addSalesCredit}
                  data-testId={`${pageId}-add-sales-credit`}
                  disabled={disabledAddButton()}
                >
                  {commonLabels.add}
                </MuiButtonSecondary>
              </MuiBox>
            </PermissionWrapper>
          </MuiBox>
        </>
      ) : null}
      <MuiBox className="sales-credit-configuration-total-amount-percentage-container">
        <MuiTypography variant="h3Bold">
          {getLabel(salesCreditSiteCoreKeys.totalPercentage)}
        </MuiTypography>

        <MuiGrid className="sales-credit-configuration-total-amount-percentage">
          <MuiTypography variant="h3Bold">
            {`${calculateTotalPercentage(filteredResults)}%`}
          </MuiTypography>
        </MuiGrid>
      </MuiBox>
      <MuiBox className="sales-credit-configuration-table">
        <MuiTable
          pageId={pageId}
          tableHeaderForm={[]}
          columns={columns}
          tableResults={filteredResults}
          noDataInApi={filteredResults.length === 0}
          noResultMessage={commonLabels.noResult}
          emptyMessage={getLabel(
            salesCreditSiteCoreKeys.noClientRelationshipOwnersAvailablePleaseAddThemManually,
          )}
          priorityResults={[]}
          isFooterVisible={false}
        />
      </MuiBox>
    </SalesCreditDetailsTableWrapper>
  );
};
